// Renders a structured product catalog emitted by the data_analyst's
// emit_product_catalog tool. Each look is a card-within-the-card with
// its own "Generate this look" button that chains into image_director —
// click → sends a structured chat message naming all products + the
// styling notes, plus implicitly the user's still-attached reference
// images (Luma image_ref).
//
// "Generate all" fires the same prompt for every look in parallel via
// one composed message that asks image_director to make N generations.

import {
  Bookmark,
  BookmarkCheck,
  ImageOff,
  Sparkles,
  Wand2,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import type { SavedProductCatalog } from "@/lib/types";

interface Product {
  // Nullable: image-only products from matrix-shape lookbooks have no
  // text brand (the photo IS the identity). When brand is null, fall
  // back to category, then to a generic "Item" label.
  brand: string | null;
  code?: string | null;
  color?: string | null;
  description?: string | null;
  category?: string | null;
  notes?: string | null;
  /** Tigris ref_id of the product's own photo (auto-extracted from a
   * cell URL in the source spreadsheet). Set when the source row had
   * an image URL that downloaded cleanly. */
  ref_id?: string | null;
  /** Fresh presigned URL for the product photo. Backend includes it
   * on emit (and re-presigns on briefing GET when reading saved
   * catalogs). Used for the thumb on the product chip. */
  ref_url?: string | null;
}

interface Look {
  id: string;
  title: string;
  styling_notes?: string | null;
  /** Tigris ref_id of the embedded xlsx image that anchored this look.
   * Set when the source file had a hero image per look. */
  hero_ref_id?: string | null;
  /** Fresh presigned URL the backend included so we can render the
   * hero thumb without a follow-up round-trip. Expires; refetch if the
   * card comes back from persisted history. */
  hero_url?: string | null;
  products: Product[];
}

interface ProductCatalog {
  id: string;
  title?: string;
  audience?: string | null;
  looks: Look[];
}

export function ProductCatalogCard({
  data,
  onSendMessage,
  briefingId,
  alreadySaved = false,
  onSaved,
}: {
  data: ProductCatalog;
  /** Called when the user hits Generate. Composes a chat message and
   * (optionally) extra ref_ids — the look's hero image is forwarded
   * as an additional ref so image_director's Luma call uses BOTH the
   * hero AND any user-attached refs as image_ref. The third arg
   * (enforcedImageRefs) makes the tool layer FORCE that ref_id list
   * onto generate_images regardless of LLM discretion. */
  onSendMessage?: (
    text: string,
    extraRefIds?: string[],
    enforcedImageRefs?: string[],
  ) => void;
  /** When set, the card shows a "Save to Shop the Look" button that
   * POSTs the catalog to /briefings/{briefingId}/catalogs. The saved
   * catalog then appears in the moodboard's "Shop the looks" tab. */
  briefingId?: string;
  /** True when this catalog id is already persisted on the briefing —
   * we surface a checkmark instead of an active save button. */
  alreadySaved?: boolean;
  /** Triggered after a successful save so the parent can refetch the
   * briefing (catalogs flow through .catalogs once the GET re-presigns
   * their hero URLs). */
  onSaved?: () => void;
}) {
  const looks = data.looks ?? [];
  const audience = data.audience ?? null;
  const [busyLookId, setBusyLookId] = useState<string | null>(null);
  const [busyAll, setBusyAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedNow, setSavedNow] = useState(false);
  const { setToast } = useStore();
  const isSaved = alreadySaved || savedNow;

  const handleSave = async () => {
    if (!briefingId || saving || isSaved) return;
    setSaving(true);
    try {
      // Cast to SavedProductCatalog — the shapes line up (looks +
      // optional metadata). Server strips hero_url before persisting.
      await api.saveBriefingCatalog(
        briefingId,
        data as unknown as SavedProductCatalog
      );
      setSavedNow(true);
      setToast({
        kind: "ok",
        text: `Catalog saved — see it in the "Shop the looks" tab.`,
      });
      onSaved?.();
    } catch (e) {
      const msg = (e as Error).message || "Save failed";
      setToast({ kind: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  if (looks.length === 0) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Product catalog emitted with no looks. The analyst may have
        misclassified the data.
      </div>
    );
  }

  const generateOne = (look: Look) => {
    if (!onSendMessage) return;
    setBusyLookId(look.id);
    // Single-look: image_ref order matters for the Luma multi-image
    // labeling rule. The frontend pre-computes the exact list so the
    // generate_images tool can ENFORCE it at the code layer — closes
    // the failure mode where the LLM silently subsetted refs.
    const extras = collectRefIds(look);
    onSendMessage(
      composeGeneratePrompt(look, audience),
      extras,
      // Enforce: same list as extras. The tool overrides the LLM's
      // ref_ids arg with this set so all refs reach Luma.
      extras.length > 0 ? extras : undefined,
    );
  };

  const generateAll = () => {
    if (!onSendMessage) return;
    setBusyAll(true);
    // "Generate all" instructs image_director to make ONE call per look
    // so each look gets its OWN ordered refs (hero + products) — we
    // don't bundle every look's refs into one call. We do NOT pass
    // enforcedImageRefs here because the agent has to pick the right
    // per-look subset for each generate_images call; a single forced
    // list would mash all looks into every call.
    const extras = looks.flatMap(collectRefIds);
    onSendMessage(
      composeGenerateAllPrompt(looks, audience),
      extras.length > 0 ? extras : undefined,
      // Multi-look: no enforcement — agent picks per look.
      undefined,
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="border-b border-border px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-1 min-w-0">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Product catalog · {looks.length} look
            {looks.length !== 1 ? "s" : ""}
          </p>
          <p className="font-medium truncate">{data.title ?? "Product catalog"}</p>
          {audience && (
            <p className="text-xs text-muted-foreground">
              Audience:{" "}
              <span className="font-mono uppercase tracking-wider">
                {audience}
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {briefingId && (
            <Button
              size="sm"
              variant={isSaved ? "secondary" : "outline"}
              onClick={handleSave}
              disabled={!briefingId || saving || isSaved}
              className="gap-1.5"
              title={
                isSaved
                  ? "This catalog is in the Shop the looks tab"
                  : "Save catalog so it appears in the Shop the looks moodboard tab"
              }
            >
              {isSaved ? (
                <BookmarkCheck className="h-3.5 w-3.5" />
              ) : (
                <Bookmark className="h-3.5 w-3.5" />
              )}
              {saving ? "Saving…" : isSaved ? "Saved" : "Save to Shop the Look"}
            </Button>
          )}
          {looks.length > 1 && (
            <Button
              size="sm"
              onClick={generateAll}
              disabled={!onSendMessage || busyAll || busyLookId !== null}
              className="gap-1.5"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {busyAll ? "Queueing…" : `Generate all ${looks.length}`}
            </Button>
          )}
        </div>
      </div>

      {/* Looks */}
      <div className="divide-y divide-border">
        {looks.map((look) => (
          <LookCard
            key={look.id}
            look={look}
            busy={busyLookId === look.id || busyAll}
            disabled={busyAll || (busyLookId !== null && busyLookId !== look.id) || !onSendMessage}
            onGenerate={() => generateOne(look)}
          />
        ))}
      </div>
    </div>
  );
}

function LookCard({
  look,
  busy,
  disabled,
  onGenerate,
}: {
  look: Look;
  busy: boolean;
  disabled: boolean;
  onGenerate: () => void;
}) {
  return (
    <div className="px-5 py-4">
      <div className="flex gap-4">
        {/* Hero thumbnail — visual confirmation of "this is the look".
            Renders the embedded xlsx hero image when one was extracted
            at upload; falls back to a "no hero" placeholder so the
            user sees that this look has no anchor image. */}
        <div className="shrink-0">
          {look.hero_url ? (
            <a
              href={look.hero_url}
              target="_blank"
              rel="noreferrer"
              className="block h-24 w-24 overflow-hidden rounded-md border border-border bg-muted"
              title="Open hero image fullsize"
            >
              <img
                src={look.hero_url}
                alt={`Hero for ${look.title}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </a>
          ) : (
            <div
              className={cn(
                "block h-24 w-24 rounded-md border border-dashed border-border",
                "bg-muted/40 flex flex-col items-center justify-center gap-1"
              )}
              title="No hero image was embedded in the source file for this look"
            >
              <ImageOff className="h-5 w-5 text-muted-foreground/60" />
              <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/70">
                no hero
              </span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="space-y-0.5 min-w-0">
              <p className="font-medium text-sm">{look.title}</p>
              {look.styling_notes && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {look.styling_notes}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant={busy ? "secondary" : "default"}
              onClick={onGenerate}
              disabled={disabled || busy}
              className="gap-1.5 shrink-0"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {busy ? "Generating…" : "Generate this look"}
            </Button>
          </div>

          {/* Products */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {look.products.map((p, i) => (
              <ProductChip key={`${look.id}-${i}`} product={p} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductChip({ product }: { product: Product }) {
  const subtitle = [product.color, product.code].filter(Boolean).join(" · ");
  // Image-only products (matrix-shape lookbooks) have no brand text.
  // The category from the slot row (Top → "TOP", Shoes → "SHOES") is
  // the next-best identity; "Item" is the last-resort label.
  const title = product.brand || product.category?.toUpperCase() || "Item";
  return (
    <div
      className={cn(
        "rounded-md border border-border bg-background/50 px-3 py-2",
        "flex gap-3 min-w-0"
      )}
      title={product.description ?? undefined}
    >
      {/* Thumb — visible when the cell-URL was extracted at upload.
          When no ref_url is set we show a "no image" placeholder so
          the user can see at a glance which products gave the agent
          an actual reference photo vs which forced it to imagine
          from text. */}
      {product.ref_url ? (
        <a
          href={product.ref_url}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 h-12 w-12 overflow-hidden rounded border border-border bg-muted"
          title="Open product photo fullsize"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={product.ref_url}
            alt={title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </a>
      ) : (
        <div
          className={cn(
            "shrink-0 h-12 w-12 rounded border border-dashed border-border",
            "bg-muted/40 flex items-center justify-center"
          )}
          title="No reference photo available — the agent will infer this product from its description"
        >
          <ImageOff className="h-4 w-4 text-muted-foreground/60" />
        </div>
      )}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-xs font-mono uppercase tracking-wider truncate">
            {title}
          </p>
          {product.brand && product.category && (
            <span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {product.category}
            </span>
          )}
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        )}
        {product.description && (
          <p className="text-xs text-foreground/70 line-clamp-2">
            {product.description}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Message composition ───────────────────────────────────────────────
// These build the chat text that the CD will route to image_director.
// We pack ALL the product detail in the message so image_director has
// everything it needs without re-reading the original Excel — and so
// the message itself is a readable artifact in the user's chat history.

function composeProductLine(p: Product): string {
  // Same fallback chain as ProductChip: brand → category → "Item". For
  // image-only products we lean on the attached ref so image_director
  // sees the actual garment instead of guessing from a generic label.
  const head = p.brand || p.category?.toUpperCase() || "Item";
  const parts: string[] = [head];
  if (p.brand && p.category) parts.push(`(${p.category})`);
  if (p.color) parts.push(`in ${p.color}`);
  if (p.description) parts.push(`— ${p.description}`);
  if (p.code) parts.push(`[${p.code}]`);
  return parts.join(" ");
}

/** Luma's image_ref array cap. Anything above this gets silently
 * dropped at the API edge, so we cull deliberately on our side. */
const LUMA_REF_CAP = 9;

/** Derive a stable per-slot grouping key for a product. Matrix-shape
 * catalogs (rows = slot types, cols = photo variants) describe each
 * product as e.g. "Top (Over) — variant 1"; strip the variant suffix
 * so all variants of the same slot collapse to one key. Fall back to
 * category for non-matrix catalogs, then to a per-index key so we
 * never accidentally merge two unrelated products. */
function slotKey(p: Product, index: number): string {
  const desc = (p.description || "").trim();
  if (desc) {
    // Strip trailing "— variant N" / "(variant N)" / ", variant N" etc.
    // Em-dash, hyphen, and parens all observed in practice.
    return desc
      .replace(/\s*[—–\-,\(]\s*variant\s+\d+\s*\)?\s*$/i, "")
      .toLowerCase();
  }
  if (p.category) return `cat:${p.category.toLowerCase()}`;
  return `idx:${index}`;
}

/** Pick which products' refs reach Luma. Under the cap we send
 * everything; over the cap we dedupe by slot so every garment gets
 * at least one reference instead of one slot crowding out another.
 * Returns the indices into look.products so the caller can keep its
 * labeling in sync. */
function selectProductIndicesForRefs(look: Look): number[] {
  const productsWithRefs = look.products
    .map((p, i) => ({ p, i }))
    .filter((x) => Boolean(x.p.ref_id));

  const heroCount = look.hero_ref_id ? 1 : 0;
  if (heroCount + productsWithRefs.length <= LUMA_REF_CAP) {
    return productsWithRefs.map((x) => x.i);
  }

  // Over cap — dedupe by slot, keep FIRST variant per slot. This
  // guarantees coverage of every distinct garment in the matrix.
  const seen = new Set<string>();
  const kept: number[] = [];
  for (const { p, i } of productsWithRefs) {
    const key = slotKey(p, i);
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(i);
    if (heroCount + kept.length >= LUMA_REF_CAP) break;
  }
  return kept;
}

/** Collect refs for a single look in image_ref ORDER: hero first,
 * then one ref per slot in catalog order. The order matches the
 * labeling the LLM is instructed to use in the Luma prompt body. */
function collectRefIds(look: Look): string[] {
  const out: string[] = [];
  if (look.hero_ref_id) out.push(look.hero_ref_id);
  for (const i of selectProductIndicesForRefs(look)) {
    const p = look.products[i];
    if (p?.ref_id) out.push(p.ref_id);
  }
  return out;
}

function composeGeneratePrompt(
  look: Look,
  audience: string | null
): string {
  const audienceLine = audience ? `Audience: ${audience}.\n` : "";
  const stylingLine = look.styling_notes
    ? `Styling: ${look.styling_notes}\n`
    : "";
  // Annotate each product line with whether it carries its own photo.
  // Order matches the image_ref order collectRefIds builds.
  const productLines = look.products
    .map((p, i) => {
      const line = composeProductLine(p);
      const refMark = p.ref_id ? " [has product photo]" : "";
      return `  ${i + 1}. ${line}${refMark}`;
    })
    .join("\n");

  // Build the explicit ref-order spec so the LLM labels each image_ref
  // role correctly in the Luma prompt body (per inku-image-prompting
  // skill rule). Order matches collectRefIds — and is culled to one
  // ref per slot when the catalog has more variants than Luma can take.
  const refLabelLines: string[] = [];
  let refIndex = 1;
  if (look.hero_ref_id) {
    refLabelLines.push(
      `  Reference ${refIndex}: this look's hero / lookbook shot — composition, casting, posture, how garments sit together.`
    );
    refIndex += 1;
  }
  const pickedIndices = selectProductIndicesForRefs(look);
  pickedIndices.forEach((productIdx) => {
    const p = look.products[productIdx];
    if (!p?.ref_id) return;
    const headLabel = p.brand || p.category?.toUpperCase() || `item ${productIdx + 1}`;
    const label = [headLabel, p.color, p.code].filter(Boolean).join(" · ");
    refLabelLines.push(
      `  Reference ${refIndex}: product ${productIdx + 1} (${label}) — exact garment fidelity (shape, fabric, color, detail).`
    );
    refIndex += 1;
  });
  // If we culled variants, tell the LLM which products were dropped
  // so the user can ask for an alternate variant next turn instead of
  // wondering why a specific handbag option never appeared.
  const droppedIndices = look.products
    .map((p, i) => ({ p, i }))
    .filter(({ p, i }) => Boolean(p.ref_id) && !pickedIndices.includes(i))
    .map(({ i }) => i);
  const droppedNote = droppedIndices.length > 0
    ? `\nDeprioritized variants (still available — user can ask to swap): ${droppedIndices
        .map((i) => {
          const p = look.products[i];
          const headLabel = p?.brand || p?.category?.toUpperCase() || `item ${i + 1}`;
          return `product ${i + 1} (${[headLabel, p?.color, p?.code].filter(Boolean).join(" · ")})`;
        })
        .join("; ")}\n`
    : "";
  const refsBlock = refLabelLines.length > 0
    ? `\nReference image order (label each one EXPLICITLY in the Luma prompt body using the "Use the {ordinal} reference for {role}" pattern):\n${refLabelLines.join("\n")}\n`
    : `\nUse the reference images attached to this thread as visual / aesthetic guidance for the styling and color palette. Label each reference's role explicitly in the Luma prompt body.`;

  return (
    `Generate one outfit image at 2:3 aspect ratio combining all of these products styled as a single look.\n` +
    audienceLine +
    `Look: ${look.title}\n` +
    stylingLine +
    `Products:\n${productLines}\n` +
    refsBlock +
    droppedNote
  );
}

function composeGenerateAllPrompt(
  looks: Look[],
  audience: string | null
): string {
  const audienceLine = audience ? `Audience: ${audience}.\n\n` : "";
  const lookBlocks = looks
    .map((look) => {
      const products = look.products
        .map((p, i) => `  ${i + 1}. ${composeProductLine(p)}`)
        .join("\n");
      const styling = look.styling_notes
        ? `Styling: ${look.styling_notes}\n`
        : "";
      const heroNote = look.hero_ref_id
        ? `Hero ref_id: ${look.hero_ref_id} (use this look's hero shot for its composition + casting).\n`
        : "";
      return `### ${look.title}\n${styling}${heroNote}Products:\n${products}`;
    })
    .join("\n\n");
  return (
    `Generate ${looks.length} outfit images at 2:3 aspect ratio — one per look listed below. ` +
    `Each is a separate single-frame styled image combining the products in that look. Make a SEPARATE generate_images call per look so each gets its OWN hero ref as image_ref (don't bundle them).\n\n` +
    audienceLine +
    lookBlocks +
    `\n\nFor each look's generation: label each Luma image_ref explicitly — first ref is that look's hero (composition + casting), subsequent refs are mood/palette/atmosphere guides.`
  );
}
